'use client'

import { useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'

import { useHydrateMap } from '@/services/queries'
import { MapFilter } from '@/components/molecules'
import type { SearchProductFilter } from '@/types/search'
import { usePropertiesOptions } from '@/services/property/usePropertiesOptions'

const DynamicMap = dynamic(() => import('@/components/molecules/MapTilerBox'), {
  ssr: false,
})

type UserPosition = {
  lat: number
  lon: number
}

type MapFilters = {
  category?: string
  startDate?: string
  endDate?: string
  status?: string
}

export const WidgetMap = ({
  filter: initialFilter,
  storageKey,
  enabled = true,
}: {
  filter: SearchProductFilter
  storageKey: string
  enabled?: boolean
}) => {
  const { actionCatalog } = usePropertiesOptions()
  const [userPosition, setUserPosition] = useState<UserPosition | undefined>(
    undefined,
  )
  const [mapFilters, setMapFilters] = useState<MapFilters>({})

  // Load user position from local storage
  useEffect(() => {
    const savedUserPosition = JSON.parse(
      localStorage.getItem('ui-map-center') ?? '{}',
    )
    const savedFilters = JSON.parse(localStorage?.getItem(storageKey) ?? '{}')
    if (savedUserPosition && Object.keys(savedUserPosition).length > 0) {
      setUserPosition(savedUserPosition)
    }
    if (savedFilters) setMapFilters(savedFilters)
  }, [storageKey])

  const filter = useMemo<SearchProductFilter>(() => {
    const and: SearchProductFilter[] = []
    if (mapFilters.startDate) {
      and.push({
        or: [
          {
            endDate: {
              ge: new Date(mapFilters.startDate).toISOString().split('T')[0],
            },
          },
          { endDate: { exists: false } },
        ],
      })
    }
    if (mapFilters.endDate) {
      and.push({
        or: [
          {
            startDate: {
              le: new Date(mapFilters.endDate).toISOString().split('T')[0],
            },
          },
          { startDate: { exists: false } },
        ],
      })
    }

    return {
      ...(and.length > 0 ? { and } : {}),
      ...(mapFilters.status && {
        action: { eq: mapFilters.status },
      }),
      ...(mapFilters.category && { category: { eq: mapFilters.category } }),
      ...initialFilter,
    }
  }, [
    mapFilters.startDate,
    mapFilters.endDate,
    mapFilters.status,
    mapFilters.category,
    initialFilter,
  ])

  const { data } = useHydrateMap({ filter, enabled })
  const { buckets = [], total } = data ?? {}

  const { categories, locations } = useMemo(() => {
    const categories =
      buckets?.find((bucket) => bucket.value === 'category')?.items || []
    const locations =
      buckets?.find((bucket) => bucket.value === 'location')?.items || []
    return { categories, locations }
  }, [buckets])

  const totalLocations = useMemo(
    () =>
      locations?.reduce(
        (acc: number, item: { count: number }) => acc + item.count,
        0,
      ) ?? 0,
    [locations],
  )

  useEffect(() => {
    if (userPosition || !data || locations.length === 0) return

    let sumLat = 0
    let sumLon = 0
    let totalCount = 0

    for (const loc of locations) {
      sumLat += loc.label.lat * loc.count
      sumLon += loc.label.lon * loc.count
      totalCount += loc.count
    }

    if (totalCount > 0) {
      const avgLat = sumLat / totalCount
      const avgLon = sumLon / totalCount
      const newPosition = { lat: avgLat, lon: avgLon }
      setUserPosition(newPosition)
      localStorage.setItem('ui-map-center', JSON.stringify(newPosition))
    }
  }, [data, locations, userPosition])

  const options = useMemo(() => {
    return categories.map((item) => ({
      value: item.label,
      label: item.label,
    }))
  }, [categories])
  const statusOptions = useMemo(
    () => actionCatalog.actionOptions.map(({ label, value }) => ({ label, value })),
    [actionCatalog.actionOptions],
  )

  const noLocations = totalLocations < (total ?? 0)
  const locationInfo = noLocations
    ? ` (${(total ?? 0) - totalLocations} sans localisation)`
    : ''
  const totalLabel = `Total sur la période : ${total ?? 0}` + locationInfo

  return (
    <section className="flex flex-col justify-center">
      <MapFilter
        setMapFilters={setMapFilters}
        mapFilters={mapFilters}
        options={options}
        statusOptions={statusOptions}
        totalLabel={totalLabel}
        storageKey={storageKey}
      />
      <div className="mb-10 mt-6 flex h-full w-full items-center justify-center rounded-lg">
        <DynamicMap
          maxZoom={14}
          minZoom={6}
          markers={locations}
          setUserPosition={setUserPosition}
          userPosition={userPosition}
          filter={filter}
        />
      </div>
    </section>
  )
}
